import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useUserByPhone(phone) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { send, isConnected, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const fetchUser = useCallback(async (attempt = 0) => {
        if (!mountedRef.current) return;

        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) {
                return;
            }

            const response = await send('cms_get_user_by_phone', { phone });
            console.log('User response:', response);

            if (!response || response.type !== 'cms_get_user_by_phone_response') {
                throw new Error('Invalid response from server');
            }

            if (!response.data) {
                throw new Error('User not found');
            }

            setUser(response.data);
            setMessage(response.message || 'User fetched successfully');
        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchUser(attempt + 1);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [phone, isConnected, connect, send]);

    // Live updates for this user
    useEffect(() => {
        const unregister = registerHandler('user_update', (msg) => {
            if (!mountedRef.current) return;
            if (msg.data && msg.data.phone === phone) {
                setUser(msg.data);
            }
        });
        return () => unregister();
    }, [registerHandler, phone]);

    // Initial fetch
    useEffect(() => {
        mountedRef.current = true;
        fetchUser(0);
        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        };
    }, [fetchUser]);

    return { user, isLoading, error, message, refetch: () => fetchUser(0) };
}
